import {
  Mail,
  Phone,
  MoreVertical,
  Edit,
  Trash2,
  Archive,
  RotateCcw,
  Globe,
  MapPin,
  Cake,
  Star,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ContactDetail } from '@/lib/api';
import { formatDate, formatContactName, getInitials, getAvatarColor } from '../utils';
import type { NameFormat, DateFormatOption } from '../types';

function ContactAvatar({ contact, size = 'sm' }: { contact: ContactDetail; size?: 'sm' | 'lg' }) {
  const dim = size === 'lg' ? 'h-14 w-14 text-xl' : 'h-9 w-9 text-sm';

  if (contact.photoUrl) {
    return (
      <img
        src={contact.photoUrl}
        alt=""
        className={`${dim} rounded-full object-cover flex-shrink-0`}
      />
    );
  }

  const initials = getInitials(contact.firstName, contact.lastName);
  const color = getAvatarColor(contact.firstName, contact.lastName);

  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
}

export { ContactAvatar };

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onArchive,
  onRestore,
  onPermanentDelete,
  onToggleFavorite,
  onShare,
  isTrashView,
  nameFormat = 'first-last',
  dateFormat = 'us',
  selectable,
  selected,
  onSelect,
}: {
  contact: ContactDetail;
  onEdit: (contact: ContactDetail) => void;
  onDelete: (contact: ContactDetail) => void;
  onArchive: (contact: ContactDetail) => void;
  onRestore?: (contact: ContactDetail) => void;
  onPermanentDelete?: (contact: ContactDetail) => void;
  onToggleFavorite?: (contact: ContactDetail) => void;
  onShare?: (contact: ContactDetail) => void;
  isTrashView?: boolean;
  nameFormat?: NameFormat;
  dateFormat?: DateFormatOption;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (contact: ContactDetail) => void;
}) {
  const displayName = formatContactName(contact, nameFormat);

  return (
    <Card
      className={`group hover:shadow-md transition-shadow cursor-pointer ${selected ? 'ring-2 ring-primary' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`Contact: ${displayName}`}
      onClick={() => {
        if (selectable && onSelect) {
          onSelect(contact);
        } else if (!isTrashView) {
          onEdit(contact);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (selectable && onSelect) {
            onSelect(contact);
          } else if (!isTrashView) {
            onEdit(contact);
          }
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex gap-3 flex-1 min-w-0">
            {selectable && (
              <input
                type="checkbox"
                checked={selected || false}
                onChange={() => onSelect?.(contact)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 h-4 w-4 rounded border-gray-300 flex-shrink-0"
              />
            )}
            <ContactAvatar contact={contact} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {contact.priorityColor && (
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: contact.priorityColor }}
                    title={contact.priorityName || 'Priority'}
                  />
                )}
                <h3 className="font-semibold truncate">
                  {displayName}
                  {contact.nickname && (
                    <span className="font-normal text-muted-foreground ml-1">
                      ({contact.nickname})
                    </span>
                  )}
                </h3>
                {contact.categoryName && (
                  <Badge
                    variant="secondary"
                    className="text-xs flex-shrink-0"
                    style={{
                      backgroundColor: contact.categoryColor
                        ? `${contact.categoryColor}20`
                        : undefined,
                      color: contact.categoryColor || undefined,
                      borderColor: contact.categoryColor ? `${contact.categoryColor}40` : undefined,
                    }}
                  >
                    {contact.categoryName}
                  </Badge>
                )}
              </div>

              {(contact.title || contact.company) && (
                <p className="text-sm text-muted-foreground mt-0.5 truncate">
                  {contact.title}
                  {contact.title && contact.company && ' at '}
                  {contact.company}
                </p>
              )}

              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                {contact.emails.length > 0 && (
                  <span className="flex items-center gap-1 truncate">
                    <Mail className="h-3 w-3 flex-shrink-0" />
                    {contact.emails[0].value}
                    {contact.emails.length > 1 && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-0.5">
                        +{contact.emails.length - 1}
                      </Badge>
                    )}
                  </span>
                )}
                {contact.phones.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3 flex-shrink-0" />
                    {contact.phones[0].value}
                    {contact.phones.length > 1 && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-0.5">
                        +{contact.phones.length - 1}
                      </Badge>
                    )}
                  </span>
                )}
                {contact.birthday && (
                  <span className="flex items-center gap-1">
                    <Cake className="h-3 w-3 flex-shrink-0" />
                    {new Date(contact.birthday + 'T00:00:00').toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                )}
                {contact.addresses?.length > 0 && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    {[contact.addresses[0].city, contact.addresses[0].state]
                      .filter(Boolean)
                      .join(', ') || contact.addresses[0].street}
                  </span>
                )}
                {contact.websites?.length > 0 && (
                  <span className="flex items-center gap-1 truncate">
                    <Globe className="h-3 w-3 flex-shrink-0" />
                    {contact.websites[0].value.replace(/^https?:\/\//, '')}
                  </span>
                )}
              </div>

              {contact.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {contact.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Custom field values */}
              {(() => {
                const cf = contact.metadata?.customFields as Record<string, unknown> | undefined;
                if (!cf || Object.keys(cf).length === 0) return null;
                const entries = Object.entries(cf).filter(([, v]) => v != null && v !== '');
                if (entries.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {entries.map(([key, val]) => (
                      <Badge key={key} variant="secondary" className="text-[10px] font-normal">
                        {key}: {String(val)}
                      </Badge>
                    ))}
                  </div>
                );
              })()}

              <p className="text-xs text-muted-foreground mt-2">
                Gathered {formatDate(contact.gatheredAt, dateFormat)}
                {contact.source && ` via ${contact.source}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0.5 flex-shrink-0">
            {!isTrashView && onToggleFavorite && (
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 transition-opacity ${
                  contact.isFavorite
                    ? 'opacity-100 text-yellow-500'
                    : 'opacity-0 group-hover:opacity-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(contact);
                }}
                title={contact.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star className={`h-4 w-4 ${contact.isFavorite ? 'fill-current' : ''}`} />
              </Button>
            )}

            {isTrashView ? (
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore?.(contact);
                  }}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  Restore
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPermanentDelete?.(contact);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Delete Forever
                </Button>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(contact)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {onShare && (
                    <DropdownMenuItem onClick={() => onShare(contact)}>
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onArchive(contact)}>
                    <Archive className="h-4 w-4 mr-2" />
                    {contact.status === 'archived' ? 'Unarchive' : 'Archive'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(contact);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
